import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Loader2, AlertTriangle, Lightbulb, ListChecks } from "lucide-react";
import { FileDropzone } from "@/components/ui/FileDropzone";
import { useClaudeAI } from "@/hooks/useClaudeAI";
import { summarizeForAI, type ParsedFile } from "@/lib/file-parsing";
import { toast } from "sonner";

const INDUSTRIES = [
  { value: "general", label: "General / Universal" },
  { value: "finance", label: "Finance" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "logistics", label: "Logistics" },
  { value: "realestate", label: "Real Estate" },
  { value: "technology", label: "Technology" },
];

interface InsightShape {
  executive_summary: string;
  key_findings: { title: string; detail: string; evidence?: string }[];
  recommendations: { action: string; expected_impact: string }[];
  anomalies?: { column: string; issue: string }[];
}

interface Props {
  onInsightsGenerated?: (insights: string) => void;
}

export const AIInsightGenerator = ({ onInsightsGenerated }: Props) => {
  const [industry, setIndustry] = useState("general");
  const [question, setQuestion] = useState("");
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [result, setResult] = useState<InsightShape | null>(null);
  const { call, isLoading } = useClaudeAI();

  const handleGenerate = async () => {
    if (!parsed) { toast.error("Upload a dataset first"); return; }
    if (!question.trim()) { toast.error("Enter a question or focus area"); return; }
    setResult(null);
    const res = await call<InsightShape>({
      feature: "insight_generator",
      input: question,
      dataset: summarizeForAI(parsed),
      options: { industry, fileName: parsed.fileName },
    });
    if (res.success && res.json) {
      setResult(res.json);
      onInsightsGenerated?.(res.response ?? "");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" /> AI Insight Generator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Industry</label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Dataset</label>
            <FileDropzone onParsed={(p) => setParsed(p)} label="Upload dataset" />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">What do you want to learn?</label>
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder="e.g. What is driving the drop in conversion rate over the last 30 days?"
          />
        </div>

        <Button onClick={handleGenerate} disabled={isLoading || !parsed || !question.trim()} className="w-full">
          {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analysing…</> : "Generate Insights"}
        </Button>

        {result && (
          <div className="space-y-4 pt-2">
            <div className="rounded-lg border bg-muted/40 p-4">
              <h4 className="font-semibold mb-1">Executive Summary</h4>
              <p className="text-sm">{result.executive_summary}</p>
            </div>

            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2"><Lightbulb className="h-4 w-4" /> Key Findings</h4>
              <ul className="space-y-2 text-sm">
                {result.key_findings?.map((f, i) => (
                  <li key={i}>
                    <span className="font-medium">{f.title}.</span> {f.detail}
                    {f.evidence && <span className="block text-xs text-muted-foreground mt-0.5">Evidence: {f.evidence}</span>}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border p-4">
              <h4 className="font-semibold mb-2 flex items-center gap-2"><ListChecks className="h-4 w-4" /> Recommendations</h4>
              <ul className="space-y-2 text-sm">
                {result.recommendations?.map((r, i) => (
                  <li key={i}><span className="font-medium">{r.action}.</span> Expected impact: {r.expected_impact}</li>
                ))}
              </ul>
            </div>

            {result.anomalies && result.anomalies.length > 0 && (
              <div className="rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:bg-yellow-950/30">
                <h4 className="font-semibold mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-yellow-600" /> Anomalies</h4>
                <ul className="text-sm space-y-1">
                  {result.anomalies.map((a, i) => <li key={i}><b>{a.column}:</b> {a.issue}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
